import { Link } from 'react-router-dom'
import { ArrowUpRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { wavePlanPath } from './plans'
import { WavePlanStatus } from './WavePlanStatus'
import {
  formatRollupDate,
  formatRollupValue,
  type RollupPlan,
} from './rollup-model'

export function WaveRollupPlanDetails({ row }: { row: RollupPlan }) {
  const sections = [
    {
      title: 'Identification',
      fields: [
        ['DPT-CLS-SCLS', row.code],
        ['Description', row.fixture.description],
        ['POG Description', row.fixture.pogDescription],
        ['Status', row.plan.status],
        ['Number of waves', row.waveCount],
      ],
    },
    {
      title: 'Location & stores',
      fields: [
        ['Location', row.fixture.location],
        ['Set Date', formatRollupDate(row.setDate)],
        ['Store Count', formatRollupValue(row.fixture.stores, 'units')],
      ],
    },
    {
      title: 'SKU & repack counts',
      fields: [
        ['Sellable SKU Count', row.fixture.sellableCount],
        ['Shipping SKU Count', row.fixture.shippingCount],
        ['PIAB SKU Count', row.fixture.piabCount],
        ['CICO SKU Count', row.fixture.cicoCount],
        ['Repack SKU Count', row.fixture.repackCount],
        [
          'Repack Ship Qty',
          formatRollupValue(row.fixture.repackShipQty, 'units'),
        ],
      ],
    },
  ]
  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 px-1.5 text-xs font-medium"
          aria-label={`Details for ${row.plan.name}`}
        >
          Details
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full gap-0 sm:w-[440px] sm:max-w-[440px]">
        <SheetHeader className="border-b p-6 pr-12">
          <SheetTitle>{row.plan.name}</SheetTitle>
          <SheetDescription>
            Plan details · {row.plan.calendarId} calendar
          </SheetDescription>
          <div className="mt-2">
            <WavePlanStatus status={row.plan.status} />
          </div>
        </SheetHeader>
        <div className="min-h-0 flex-1 space-y-6 overflow-y-auto p-6">
          {sections.map(section => (
            <section key={section.title}>
              <h3 className="mb-3 text-sm font-semibold">{section.title}</h3>
              <dl className="space-y-3 text-sm">
                {section.fields.map(([label, value]) => (
                  <div
                    key={label}
                    className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] gap-4"
                  >
                    <dt className="text-muted-foreground">{label}</dt>
                    <dd
                      className="break-words font-medium tabular-nums"
                      data-detail={label}
                    >
                      {value ?? '—'}
                    </dd>
                  </div>
                ))}
              </dl>
            </section>
          ))}
          <p className="text-xs leading-relaxed text-muted-foreground">
            Wave SKU Count counts shipping lines per wave. Its season total is
            the plan’s Sellable SKU Count; counts across waves are not additive.
            Cube includes all distribution centers.
          </p>
        </div>
        <div className="border-t p-4">
          <Button asChild className="w-full">
            <Link to={wavePlanPath(row.plan.id)}>
              Open plan <ArrowUpRight size={16} />
            </Link>
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  )
}
